import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { isFamilyMembershipMember } from '../../../../utils/membershipReporting';

const TYPE_LABELS = {
  individual: 'Individual',
  family_owner: 'Family',
  family_covered: 'Family',
};

function normalizeId(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeIdList(value) {
  return Array.isArray(value)
    ? Array.from(new Set(value.map(normalizeId).filter(Boolean)))
    : [];
}

function hasFamilyCoverage(member = {}) {
  return isFamilyMembershipMember(member);
}

function buildFamilyTierLabelMap(familyTiers = []) {
  return Object.fromEntries(
    (Array.isArray(familyTiers) ? familyTiers : [])
      .map((tier) => [
        normalizeId(tier?.id),
        typeof tier?.label === 'string' ? tier.label.trim() : '',
      ])
      .filter(([id, label]) => Boolean(id && label))
  );
}

function getStoredPlanLabel(member = {}) {
  const snapshotLabel = typeof member.membershipPlanSnapshot?.label === 'string'
    ? member.membershipPlanSnapshot.label.trim()
    : '';
  const direct = typeof member.membershipPlanLabel === 'string' ? member.membershipPlanLabel.trim() : '';
  return snapshotLabel || direct;
}

function isIndividualPlanLabel(value) {
  return /^individual membership$/i.test(typeof value === 'string' ? value.trim() : '');
}

function getPlanLabel(member = {}, familyTierLabels = {}) {
  const planType = normalizeId(member.membershipPlanType).toLowerCase();
  const tierId = normalizeId(member.membershipPlanTierId);
  const storedPlanLabel = getStoredPlanLabel(member);
  const familyCovered = hasFamilyCoverage(member);

  if (planType === 'family_tier' || familyCovered) {
    if (tierId && familyTierLabels[tierId]) return familyTierLabels[tierId];
    if (storedPlanLabel && !isIndividualPlanLabel(storedPlanLabel)) return storedPlanLabel;
    return 'Family Membership';
  }

  if (storedPlanLabel) return storedPlanLabel;
  return hasFamilyCoverage(member) ? 'Family Membership' : 'Individual Membership';
}

function getFamilyId(member = {}) {
  return normalizeId(member.membershipCoverageFamilyId) || normalizeId(member.membershipCoveredByFamilyId);
}

function getFamilyRoleIds(member = {}) {
  const roles = member?.familyRoles && typeof member.familyRoles === 'object' ? member.familyRoles : {};
  return Object.keys(roles).map(normalizeId).filter(Boolean);
}

function getFamilyRole(member = {}, familyId = '') {
  const normalizedFamilyId = normalizeId(familyId);
  const roles = member?.familyRoles && typeof member.familyRoles === 'object' ? member.familyRoles : {};
  return normalizedFamilyId ? normalizeId(roles[normalizedFamilyId]).toLowerCase() : '';
}

function inferSharedFamilyId(memberMap, memberIds = []) {
  const counts = new Map();
  memberIds.forEach((uid) => {
    const member = memberMap.get(uid);
    getFamilyRoleIds(member).forEach((familyId) => {
      counts.set(familyId, (counts.get(familyId) || 0) + 1);
    });
  });

  const shared = Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  return shared[0]?.[0] || '';
}

function getCoverageKind(member = {}) {
  if (!hasFamilyCoverage(member)) return 'individual';
  const memberId = normalizeId(member.id);
  const coveredByUid = normalizeId(member.membershipCoveredByUid);
  const paymentOwnerUid = normalizeId(member.membershipPaymentOwnerUid);
  const coveredIds = normalizeIdList(member.membershipCoveredMemberIds);
  const coverageType = normalizeId(member.membershipCoverageType).toLowerCase();
  const planType = normalizeId(member.membershipPlanType).toLowerCase();

  if (coveredByUid && coveredByUid !== memberId) return 'family_covered';

  if (
    coverageType === 'family'
    || planType === 'family_tier'
    || coveredIds.length > 1
    || (paymentOwnerUid && paymentOwnerUid === memberId && coveredIds.length > 0)
  ) {
    return 'family_owner';
  }

  return 'individual';
}

function nameFromUserDoc(user = {}, fallback = '') {
  return user.displayName
    || [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    || user.username
    || fallback;
}

function cleanDisplayName(value) {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return '';
  return /^unknown (member|payer)$/i.test(name) ? '' : name;
}

function memberDisplayName(member = {}) {
  const name = cleanDisplayName(member.name);
  const memberId = normalizeId(member.id);
  return name && name !== memberId ? name : 'Member';
}

function nameFor(memberMap, uid, extraNames = {}) {
  const member = memberMap.get(uid);
  const normalizedUid = normalizeId(uid);
  const memberName = cleanDisplayName(member?.name);
  const extraName = cleanDisplayName(extraNames[uid]);
  if (memberName && memberName !== normalizedUid) return memberName;
  if (extraName && extraName !== normalizedUid) return extraName;
  return '';
}

function familyLabelFor(familyId, familyNames = {}) {
  if (!familyId) return 'Family membership';
  return familyNames[familyId] || 'Family membership';
}

function inferVisibleFamilyOwnerId(memberRows = [], memberMap, familyId = '') {
  const normalizedFamilyId = normalizeId(familyId);
  if (!normalizedFamilyId) return '';

  const candidates = memberRows
    .map((row) => {
      const member = memberMap.get(row.id);
      if (!member || !getFamilyRoleIds(member).includes(normalizedFamilyId)) return null;
      const role = getFamilyRole(member, normalizedFamilyId);
      const isCovered = row.kind === 'family_covered';
      const isPaymentOwner = normalizeId(member.membershipPaymentOwnerUid) === row.id;
      const isFamilyPlanOwner = normalizeId(member.membershipPlanType).toLowerCase() === 'family_tier';
      const roleScore = role === 'parent' || role === 'guardian' || role === 'adult'
        ? 4
        : role === 'child'
          ? 0
          : 2;
      const coverageScore = isFamilyPlanOwner || isPaymentOwner
        ? 4
        : isCovered
          ? 0
          : 2;
      return {
        id: row.id,
        name: row.name,
        score: roleScore + coverageScore,
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.score - left.score || left.name.localeCompare(right.name));

  return candidates[0]?.id || '';
}

export function buildBreakdown(members = [], familyNames = {}, extraNames = {}, familyTiers = []) {
  const memberMap = new Map(members.map((member) => [member.id, member]));
  const familyTierLabels = buildFamilyTierLabelMap(familyTiers);
  const memberRows = members.map((member) => {
    const kind = getCoverageKind(member);
    const ownerId = kind === 'family_covered'
      ? normalizeId(member.membershipCoveredByUid) || normalizeId(member.membershipPaymentOwnerUid)
      : normalizeId(member.id);
    const familyId = getFamilyId(member);
    const familyLabel = familyLabelFor(familyId, familyNames);
    const ownerName = kind === 'family_covered' ? nameFor(memberMap, ownerId, extraNames) : '';

    return {
      id: member.id,
      name: memberDisplayName(member),
      kind,
      statusLabel: TYPE_LABELS[kind],
      planLabel: getPlanLabel(member, familyTierLabels),
      storedPlanLabel: getStoredPlanLabel(member),
      ownerId,
      ownerName,
      familyId,
      familyLabel,
      coveredIds: normalizeIdList(member.membershipCoveredMemberIds),
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  const groupsByKey = new Map();
  const ensureGroup = (key, seed = {}) => {
    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, {
        key,
        ownerId: seed.ownerId || '',
        planLabel: seed.planLabel || 'Family Membership',
        familyId: seed.familyId || '',
        coveredIds: new Set(),
        storedPlanLabels: new Set(),
      });
    }
    const group = groupsByKey.get(key);
    if (!group.ownerId && seed.ownerId) group.ownerId = seed.ownerId;
    if ((!group.planLabel || group.planLabel === 'Family Membership') && seed.planLabel) {
      group.planLabel = seed.planLabel;
    }
    if (!group.familyId && seed.familyId) group.familyId = seed.familyId;
    if (seed.storedPlanLabel) group.storedPlanLabels.add(seed.storedPlanLabel);
    return group;
  };

  memberRows.forEach((row) => {
    if (row.kind === 'family_owner') {
      const key = row.ownerId || row.familyId || row.id;
      const group = ensureGroup(key, row);
      const ids = row.coveredIds.length > 0 ? row.coveredIds : [row.id];
      ids.forEach((uid) => group.coveredIds.add(uid));
      if (row.ownerId) group.coveredIds.add(row.ownerId);
    }

    if (row.kind === 'family_covered') {
      const key = row.ownerId || row.familyId || row.id;
      const group = ensureGroup(key, row);
      group.coveredIds.add(row.id);
      if (row.ownerId) group.coveredIds.add(row.ownerId);
    }
  });

  const familyGroups = Array.from(groupsByKey.values()).map((group) => {
    const initialCoveredIds = Array.from(group.coveredIds).filter(Boolean);
    const coveredIdSet = new Set(initialCoveredIds);
    const familyId = group.familyId || inferSharedFamilyId(memberMap, initialCoveredIds);
    const currentOwnerName = nameFor(memberMap, group.ownerId, extraNames);
    const inferredOwnerId = currentOwnerName
      ? ''
      : inferVisibleFamilyOwnerId(memberRows, memberMap, familyId);
    const ownerId = inferredOwnerId || group.ownerId;
    if (ownerId) coveredIdSet.add(ownerId);
    const coveredIds = Array.from(coveredIdSet);
    const familyLabel = familyLabelFor(familyId, familyNames);
    const coveredNames = coveredIds
      .map((uid) => nameFor(memberMap, uid, extraNames))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    const ownerName = nameFor(memberMap, ownerId, extraNames);
    const storedPlanLabels = Array.from(group.storedPlanLabels || [])
      .filter((label) => label && label !== group.planLabel)
      .sort((a, b) => a.localeCompare(b));

    return {
      ...group,
      ownerId,
      familyId,
      coveredIds,
      ownerName: ownerName || 'Membership payer',
      familyLabel,
      coveredNames,
      paidByName: ownerName || 'Membership payer',
      storedPlanLabels,
    };
  }).sort((a, b) => a.ownerName.localeCompare(b.ownerName));

  const familyLabelByKey = new Map(familyGroups.map((group) => [group.key, group.familyLabel]));
  const familyGroupByOwnerId = new Map(
    familyGroups
      .filter((group) => group.ownerId)
      .map((group) => [group.ownerId, group])
  );
  const rowsWithFamilyLabels = memberRows.map((row) => {
    const ownerGroup = familyGroupByOwnerId.get(row.id);
    const member = memberMap.get(row.id) || {};
    const explicitlyIndividual = normalizeId(member.membershipPlanType).toLowerCase() === 'individual'
      || normalizeId(member.membershipCoverageType).toLowerCase() === 'individual';
    if (row.kind === 'individual' && ownerGroup && !explicitlyIndividual) {
      return {
        ...row,
        kind: 'family_owner',
        statusLabel: TYPE_LABELS.family_owner,
        planLabel: ownerGroup.planLabel || row.planLabel,
        ownerId: row.id,
        ownerName: '',
        familyId: ownerGroup.familyId || row.familyId,
        familyLabel: ownerGroup.familyLabel || row.familyLabel,
        coveredIds: ownerGroup.coveredIds,
      };
    }
    if (row.kind === 'individual') return row;
    const key = row.ownerId || row.familyId || row.id;
    return {
      ...row,
      familyLabel: familyLabelByKey.get(key) || row.familyLabel,
    };
  });

  return { memberRows: rowsWithFamilyLabels, familyGroups };
}

export default function MembershipBreakdownModal({
  isOpen,
  onClose,
  teamId,
  members = [],
  familyTiers = [],
}) {
  const [familyNames, setFamilyNames] = useState({});
  const [extraNames, setExtraNames] = useState({});

  const extraNameIdsKey = useMemo(() => {
    const knownIds = new Set(members.map((member) => normalizeId(member.id)).filter(Boolean));
    const ids = new Set();
    members.forEach((member) => {
      [
        normalizeId(member.membershipCoveredByUid),
        normalizeId(member.membershipPaymentOwnerUid),
        ...normalizeIdList(member.membershipCoveredMemberIds),
      ].forEach((uid) => {
        if (uid && !knownIds.has(uid)) ids.add(uid);
      });
    });
    return Array.from(ids).sort().join('|');
  }, [members]);

  const familyIdsKey = useMemo(() => {
    const ids = Array.from(new Set(
      members.flatMap((member) => [
        getFamilyId(member),
        ...getFamilyRoleIds(member),
      ]).filter(Boolean)
    )).sort();
    return ids.join('|');
  }, [members]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    const familyIds = familyIdsKey ? familyIdsKey.split('|').filter(Boolean) : [];
    if (familyIds.length === 0) {
      setFamilyNames({});
      return;
    }

    let cancelled = false;
    Promise.all(
      familyIds.map(async (familyId) => {
        const snap = await getDoc(doc(db, 'families', familyId)).catch(() => null);
        const name = snap?.exists() ? String(snap.data()?.name || '').trim() : '';
        return [familyId, name && name !== familyId ? name : 'Family membership'];
      })
    ).then((entries) => {
      if (!cancelled) setFamilyNames(Object.fromEntries(entries));
    }).catch(() => {
      if (!cancelled) setFamilyNames({});
    });

    return () => {
      cancelled = true;
    };
  }, [familyIdsKey, isOpen, teamId]);

  useEffect(() => {
    if (!isOpen) return;
    const userIds = extraNameIdsKey ? extraNameIdsKey.split('|').filter(Boolean) : [];
    if (userIds.length === 0) {
      setExtraNames({});
      return;
    }

    let cancelled = false;
    Promise.all(
      userIds.map(async (uid) => {
        const snap = await getDoc(doc(db, 'users', uid)).catch(() => null);
        const name = snap?.exists() ? nameFromUserDoc(snap.data() || '') : '';
        return [uid, name];
      })
    ).then((entries) => {
      if (cancelled) return;
      setExtraNames(Object.fromEntries(entries.filter(([, name]) => Boolean(name))));
    }).catch(() => {
      if (!cancelled) setExtraNames({});
    });

    return () => {
      cancelled = true;
    };
  }, [extraNameIdsKey, isOpen]);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [familyPlanFilter, setFamilyPlanFilter] = useState('all');

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setTypeFilter('all');
      setFamilyPlanFilter('all');
    }
  }, [isOpen]);

  const { memberRows, familyGroups } = useMemo(
    () => buildBreakdown(members, familyNames, extraNames, familyTiers),
    [extraNames, familyNames, familyTiers, members]
  );

  const familyMemberCount = useMemo(() => (
    new Set(familyGroups.flatMap((group) => group.coveredIds)).size
  ), [familyGroups]);

  const summary = useMemo(() => ({
    total: memberRows.length,
    individual: Math.max(0, memberRows.length - familyMemberCount),
    family: familyMemberCount,
    families: familyGroups.length,
  }), [familyGroups.length, familyMemberCount, memberRows.length]);
  const hasConfiguredFamilyMemberships = Array.isArray(familyTiers) && familyTiers.length > 0;
  const showFamilyMemberships = hasConfiguredFamilyMemberships || familyGroups.length > 0;

  const familyTypeGroups = useMemo(() => {
    const byPlan = new Map();
    familyGroups.forEach((group) => {
      const planLabel = group.planLabel || 'Family Membership';
      if (!byPlan.has(planLabel)) {
        byPlan.set(planLabel, {
          planLabel,
          familyCount: 0,
          memberCount: 0,
        });
      }
      const entry = byPlan.get(planLabel);
      entry.familyCount += 1;
      entry.memberCount += group.coveredIds.length;
    });
    return Array.from(byPlan.values())
      .sort((a, b) => b.familyCount - a.familyCount || a.planLabel.localeCompare(b.planLabel));
  }, [familyGroups]);

  useEffect(() => {
    if (
      familyPlanFilter !== 'all'
      && !familyTypeGroups.some((group) => group.planLabel === familyPlanFilter)
    ) {
      setFamilyPlanFilter('all');
    }
    if (!showFamilyMemberships && familyPlanFilter !== 'all') {
      setFamilyPlanFilter('all');
    }
  }, [familyPlanFilter, familyTypeGroups, showFamilyMemberships]);

  useEffect(() => {
    if (!showFamilyMemberships && typeFilter === 'family') {
      setTypeFilter('all');
    }
  }, [showFamilyMemberships, typeFilter]);

  const filteredFamilyGroups = useMemo(() => (
    familyPlanFilter === 'all'
      ? familyGroups
      : familyGroups.filter((group) => group.planLabel === familyPlanFilter)
  ), [familyGroups, familyPlanFilter]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return memberRows.filter((row) => {
      if (typeFilter === 'individual' && row.kind !== 'individual') return false;
      if (typeFilter === 'family' && row.kind === 'individual') return false;
      if (!needle) return true;
      return [
        row.name,
        row.statusLabel,
        row.planLabel,
        row.ownerName,
        row.familyLabel,
      ].join(' ').toLowerCase().includes(needle);
    });
  }, [memberRows, search, typeFilter]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content bolt-on-insights-modal membership-breakdown-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Membership breakdown"
      >
        <div className="bolt-on-insights-modal__header">
          <div>
            <h2 className="modal-title">Membership breakdown</h2>
            <p className="bolt-on-insights-modal__subtitle">
              {showFamilyMemberships
                ? 'Active individual and family memberships for this club.'
                : 'Active individual memberships for this club.'}
            </p>
          </div>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Close
          </button>
        </div>

        <section className="membership-breakdown-kpis" aria-label="Membership summary">
          <div className="membership-breakdown-kpi">
            <span>Total active</span>
            <strong>{summary.total}</strong>
          </div>
          <div className="membership-breakdown-kpi">
            <span>Individual</span>
            <strong>{summary.individual}</strong>
          </div>
          {showFamilyMemberships && (
            <>
              <div className="membership-breakdown-kpi">
                <span>Family members</span>
                <strong>{summary.family}</strong>
              </div>
              <div className="membership-breakdown-kpi">
                <span>Families</span>
                <strong>{summary.families}</strong>
              </div>
            </>
          )}
        </section>

        {showFamilyMemberships && familyTypeGroups.length > 0 && (
          <section className="bolt-on-insights-panel">
            <h3 className="bolt-on-insights-panel__title">Family membership types</h3>
            <div className="membership-family-type-list">
              <button
                type="button"
                className={`membership-family-type-card${familyPlanFilter === 'all' ? ' is-selected' : ''}`}
                onClick={() => setFamilyPlanFilter('all')}
                aria-pressed={familyPlanFilter === 'all'}
              >
                <span>All family types</span>
                <strong>
                  {familyGroups.length} famil{familyGroups.length === 1 ? 'y' : 'ies'}
                </strong>
                <small>
                  {summary.family} covered member{summary.family === 1 ? '' : 's'}
                </small>
              </button>
              {familyTypeGroups.map((group) => (
                <button
                  key={group.planLabel}
                  type="button"
                  className={`membership-family-type-card${familyPlanFilter === group.planLabel ? ' is-selected' : ''}`}
                  onClick={() => setFamilyPlanFilter(group.planLabel)}
                  aria-pressed={familyPlanFilter === group.planLabel}
                >
                  <span>{group.planLabel}</span>
                  <strong>
                    {group.familyCount} famil{group.familyCount === 1 ? 'y' : 'ies'}
                  </strong>
                  <small>
                    {group.memberCount} covered member{group.memberCount === 1 ? '' : 's'}
                  </small>
                </button>
              ))}
            </div>
          </section>
        )}

        {showFamilyMemberships && (
          <section className="bolt-on-insights-panel">
            <h3 className="bolt-on-insights-panel__title">
              Families{familyPlanFilter !== 'all' ? ` - ${familyPlanFilter}` : ''}
            </h3>
            {familyGroups.length === 0 ? (
              <p className="text-muted" style={{ margin: 0 }}>No family memberships found.</p>
            ) : (
              <div className="membership-family-list">
                {filteredFamilyGroups.map((group) => (
                  <details key={group.key} className="membership-family-card">
                    <summary>
                      <span className="membership-family-card__name">{group.familyLabel}</span>
                      <span className="membership-family-card__meta">
                        {group.coveredNames.length} member{group.coveredNames.length === 1 ? '' : 's'} • {group.planLabel}
                      </span>
                    </summary>
                    <dl className="membership-family-card__details">
                      <div>
                        <dt>Paid by</dt>
                        <dd>{group.paidByName}</dd>
                      </div>
                      <div>
                        <dt>Covered members</dt>
                        <dd>{group.coveredNames.join(', ') || 'No visible covered members'}</dd>
                      </div>
                      {group.storedPlanLabels.length > 0 && (
                        <div>
                          <dt>Stored plan</dt>
                          <dd>{group.storedPlanLabels.join(', ')}</dd>
                        </div>
                      )}
                    </dl>
                  </details>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="bolt-on-insights-panel">
          <div className="bolt-on-insights-toolbar membership-breakdown-toolbar">
            <h3 className="bolt-on-insights-panel__title">Members</h3>
            <div className="membership-breakdown-controls">
              <input
                type="search"
                className="bolt-on-insights-search"
                placeholder="Search members or plans"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <select
                className="membership-breakdown-select"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                aria-label="Filter membership type"
              >
                <option value="all">All types</option>
                <option value="individual">Individual</option>
                {showFamilyMemberships && <option value="family">Family</option>}
              </select>
            </div>
          </div>
          <div className="bolt-on-insights-modal__table-wrap">
            <table className="bolt-on-insights-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Type</th>
                  <th>Plan</th>
                  {showFamilyMemberships && <th>Family</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={showFamilyMemberships ? 4 : 3} className="text-muted bolt-on-insights-table__empty">No members match that filter.</td>
                  </tr>
                ) : filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Member">{row.name}</td>
                    <td data-label="Type">{row.statusLabel}</td>
                    <td data-label="Plan">{row.planLabel}</td>
                    {showFamilyMemberships && <td data-label="Family">{row.kind === 'individual' ? 'N/A' : row.familyLabel}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>,
    document.body
  );
}

MembershipBreakdownModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  teamId: PropTypes.string.isRequired,
  members: PropTypes.array,
  familyTiers: PropTypes.array,
};
